import React, { useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCDropDownItem, RCCGrid, RCCHidden, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import SortableContainer from "src/components/Sortable/Container";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import { IOftbm002 } from "../../interface";
import ModalAddChangeDeleteLines from "../ModalAddChangeDeleteLines";
import { IRefModalAddChangeDeleteLines } from "../ModalAddChangeDeleteLines/interface";
import ModalManageIndicator from "../ModalManageIndicator";
import { IRefModalManageIndicator } from "../ModalManageIndicator/interface";
import { IComponentLines, IOftbm002Item } from "./interface";
import cardStyle from "./styles";

const returnOrigin = (origin: number, translate: (text: string, objKeys?: object | undefined) => string) => {
	switch (origin)
	{
	case 1:
		return translate("Receita de vendas");
	case 2:
		return translate("Dedução de vendas");
	case 3:
		return translate("Custos variáveis");
	case 4:
		return translate("Despesas variáveis");
	case 5:
		return translate("Despesas operacionais");
	case 6:
		return translate("Outras despesas");
	case 90:
		return translate("Fórmula");
	case 91:
		return translate("Impostos");
	default:
		return translate("Nenhuma");
	}
};

const Lines: React.FC<IComponentLines> = ({
	lines,
	ofm001Id,
	budgetPlanId,
	reloadLines,
	useApportionment
}) => {
	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const [internalLines, setInternalLines] = useState<IOftbm002Item[]>(
		lines.map((line) => ({
			...line,
			id: v4()
		}))
	);

	const modalAddChangeDeleteLinesRef = useRef<IRefModalAddChangeDeleteLines>(null);
	const refModalManageIndicator = useRef<IRefModalManageIndicator>(null);

	const handleDragEnd = async (activeId: string, overId: string) => {
		const activeIndex = internalLines.findIndex((checks) => checks.id === activeId);
		const overIndex = internalLines.findIndex((checks) => checks.id === overId);
		const prevInternalLines = [...internalLines];
		const internalLinesAfterMove = arrayMove(prevInternalLines, activeIndex, overIndex);
		setInternalLines(internalLinesAfterMove);
		try{
			await RCCServicesApi.putRequest({
				method: "IncomeStatement/ReorderLines",
				body: internalLinesAfterMove
			});
		}catch(err){
			setInternalLines(prevInternalLines);
			standardErrorMessages(err, translate("Erro ao reordenar linhas"), addToast);
		}
	};

	const addIndicator = async (ofm002Id: number) => {
		try{        
			await RCCServicesApi.postRequest({
				method: "IncomeStatement/InsertIndicator",
				urlParameters: {
					ofa001Id: budgetPlanId,
					ofm002Id,
					ofa002Id: "",
					currentLanguage
				}
			});
			setInternalLines((prevState) => prevState.map((line) => {
				if(ofm002Id === line.ofm002Id)
					return { ...line, ofm002Iqc001Id: 1 };
				return line;
			}));
			addToast({
				title: translate("Sucesso"),
				description: translate("Indicador adicionado com sucesso"),
				color: "green"
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar indicador"), addToast);
		}
	};

	// CALLBACKS
	const onCallbackAdd = async (dt: IOftbm002) => {
		if(dt.ofm002Baseav === "S"){
			const data = await reloadLines();
			setInternalLines(data.map((line) => ({
				...line,
				id: v4()
			})));
			return;
		}
		setInternalLines((prevState) => [...prevState, {
			...dt,
			id: v4()
		}]);
	};
	const onCallbackChange = async (dt: IOftbm002) => {
		if(dt.ofm002Baseav === "S"){
			const data = await reloadLines();
			setInternalLines(data.map((line) => ({
				...line,
				id: v4()
			})));
			return;
		}
		setInternalLines((prevState) => prevState.map((line) => {
			if(dt.ofm002Id === line.ofm002Id)
				return { ...dt, id: v4() };
			return line;
		}));
	};
	const onCallbackDelete = (id: number) => {
		setInternalLines((prevState) => prevState.filter((line) => line.ofm002Id !== id));
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				<RCCGrid
					container
					item
				>
					<RCCGrid
						container
						item
						justifyContent="space-between"
					>
						<Title color="purple" marginBottom={16} marginTop={8}>
							{translate("Linhas do demonstrativo")}
						</Title>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar linha")}
							position="left"
						>
							<RCCButton
								color="purple"
								icon={{
									name: "plus"
								}}
								layout="isRounded"
								size="md"
								onClick={() => modalAddChangeDeleteLinesRef.current?.actionButtonAdd(onCallbackAdd)}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
				<RCCGrid
					container
					item
					style={{ position: "relative" }}
				>
					<RCCHidden smDown>
						<RCCGrid
							container
							item
							alignItems="center"
							styleInline={`${cardStyle} background: var(--li-header-color); font-weight: bold;border-top: 1px solid var(--border-color); position: sticky; top: 0; z-index: 1;`}
						>										
							<RCCGrid
								container
								item
								xs={12}
								sm={7}
							>
								{translate("Descrição")}
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={6}
								sm={2}
							>
								{translate("Origem")}
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={1}
								sm={1}
								justifyContent="center"
							>
								{translate("Mostra")}
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={1}
								sm={1}
								justifyContent="center"
							>
								{translate("Destaca")}
							</RCCGrid>
						</RCCGrid>	
					</RCCHidden>
					
					<SortableContainer 
						items={internalLines}
						onDragEnd={handleDragEnd}
					>	
						{internalLines.map((miles: IOftbm002Item, index: number) => (
							<SortableItem id={miles.id} key={miles.id}>
								<RCCGrid
									container
									item
									alignItems="center"
									styleInline={`${cardStyle} ${index % 2 === 0 ? "background: var(--li-child-striped);" : "background: var(--li-child-color);"}`}
								>										
									<RCCGrid
										container
										item
										xs={12}
										sm={7}
										alignItems="center"
										style={{
											fontWeight: miles.ofm002Destacalinha === "S" ? "bold" : "normal",
											color: miles.ofm002Mostralinha === "S" ? "var(--default-color-font)" : "var(--grey)",
											overflow: "hidden"
										}}
									>
										{miles.ofm002Descricao}
										{miles.ofm002Baseav === "S" && (
											<RCCBadge 
												color="green"
												styleInline="font-size: 0.75rem; padding-top: 2px; padding-bottom: 2px; margin-left: 1rem;"
											>{translate("Base Análise Vertical")}</RCCBadge>
										)}
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={6}
										sm={2}
									>
										{returnOrigin(miles.ofm002Origem, translate)}
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={1}
										sm={1}
										justifyContent="center"
									>
										{miles.ofm002Mostralinha === "S" && (
											<RCCIcon 
												name="checkCircle"
												color="var(--green)"
											/>
										)}
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={1}
										sm={1}
										justifyContent="center"
									>
										{miles.ofm002Destacalinha === "S" && (
											<RCCIcon 
												name="checkCircle"
												color="var(--green)"
											/>
										)}
									</RCCGrid>
									<RCCGrid
										container
										item
										justifyContent="flex-end"
										xs={4}
										sm={1}
									>
										<SortableHandle>
											<RCCTooltip
												color="grey"
												text={translate("Ordenar")}
											>
												<RCCButton
													color="transparent"
													icon={{
														name: "arrows",
														color: "var(--grey)"
													}}
													layout="isRounded"
													tabIndex={-1}
												/>
											</RCCTooltip>	
										</SortableHandle>
									
										<OptionsColumn position="leftEnd">
											<RCCDropDownItem
												selfEnclose
												onClick={() => modalAddChangeDeleteLinesRef.current?.actionButtonChange(miles.ofm002Id, onCallbackChange)}
											>
												<RCCIcon name="pencil" />
												{translate("Alterar")}
											</RCCDropDownItem>
											{miles.ofm002Origem !== 90 && (
												<>
													<RCCDropDownItem
														selfEnclose
														onClick={() => modalAddChangeDeleteLinesRef.current?.actionButtonValues(miles.ofm002Id, miles.ofm002Origem, miles.ofm002Descricao, useApportionment)}
													>
														<RCCIcon name="plus" />
														{translate("Valores")}
													</RCCDropDownItem>
													<RCCDropDownItem
														selfEnclose
														onClick={() => modalAddChangeDeleteLinesRef.current?.actionButtonDrillDown(miles.ofm002Id, miles.ofm002Origem)}
													>
														<RCCIcon name="arrowRight" />
														Drill-down
													</RCCDropDownItem>
												</>
											)}
											{miles.ofm002Iqc001Id === null && <RCCDropDownItem
												selfEnclose
												onClick={() => addIndicator(miles.ofm002Id)}
											>
												<RCCIcon name="plus" />
												{translate("Adicionar indicador Total")}
											</RCCDropDownItem>}
											<RCCDropDownItem
												selfEnclose
												onClick={() => refModalManageIndicator.current?.actionButtonManageIndicators(miles.ofm002Id, miles.ofm002Descricao)}
											>
												<RCCIcon name="chartBar" />
												{translate("Gerenciar indicadores")}
											</RCCDropDownItem>
											{internalLines.length > 1 && (
												<RCCDropDownItem
													selfEnclose
													onClick={() => modalAddChangeDeleteLinesRef.current?.actionButtonDelete(miles.ofm002Id, onCallbackDelete)}
												>
													<RCCIcon name="trash" />
													{translate("Excluir")}
												</RCCDropDownItem>
											)}
										</OptionsColumn>
									
									</RCCGrid>
								</RCCGrid>											
							</SortableItem>
						))}
					</SortableContainer>
				</RCCGrid>
			</RCCGrid>
		
			<ModalAddChangeDeleteLines 
				ref={modalAddChangeDeleteLinesRef}
				ofm001Id={ofm001Id}
				budgetPlanId={budgetPlanId}
			/>

			<ModalManageIndicator 
				ref={refModalManageIndicator}
				budgetPlanId={budgetPlanId}
			/>
		</>
	);
};

export default Lines;
